import { c as _c } from "react/compiler-runtime";
import type { AsanaIssue } from '@/lib/asana/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

// Define response types for clarity and type safety
export type AsanaAssociatedIssueGetResponse = {
  issue: AsanaIssue | null;
};
export type AsanaAssociatedIssueSetResponse = {
  result: AsanaIssue;
};
export type AsanaAssociatedIssueDeleteResponse = {
  success: boolean | null;
};
export type SetIssueParams = {
  issueId: string;
  issue: AsanaIssue;
};
export function useAssociatedAsanaIssue(owner, repo, number, options) {
  const $ = _c(54);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = ["associated-asana-issue", owner, repo, number];
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const queryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = async () => {
      const res = await fetch(`/api/asana/associated-issue/get?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}&number=${number}`);
      if (!res.ok) {
        throw Object.assign(new Error("Failed to fetch Asana issue"), {
          status: res.status
        });
      }
      const parsed = (await res.json()) as AsanaAssociatedIssueGetResponse;
      return parsed.issue;
    };
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const t2 = Boolean(owner && repo && number) && options?.enabled !== false;
  let t3;
  if ($[8] !== queryKey || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey,
      queryFn: t1,
      enabled: t2,
      retry: false
    };
    $[8] = queryKey;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    data,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery(t3);
  let t4;
  if ($[12] !== number || $[13] !== owner || $[14] !== repo) {
    t4 = async t5 => {
      const {
        issueId
      } = t5;
      const res_0 = await fetch("/api/asana/associated-issue/set", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          owner,
          repo,
          number,
          issueId
        })
      });
      if (!res_0.ok) {
        throw new Error("Failed to set Asana issue");
      }
      return (await res_0.json()) as AsanaAssociatedIssueSetResponse;
    };
    $[12] = number;
    $[13] = owner;
    $[14] = repo;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  let t6;
  let t7;
  if ($[16] !== queryClient || $[17] !== queryKey) {
    t5 = async t8 => {
      const {
        issue
      } = t8;
      await queryClient.cancelQueries({
        queryKey
      });
      const previousIssue = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, issue);
      return {
        previousIssue
      };
    };
    t6 = (_err, _variables, context) => {
      if (context?.previousIssue !== undefined) {
        queryClient.setQueryData(queryKey, context.previousIssue);
      }
    };
    t7 = () => {
      queryClient.invalidateQueries({
        queryKey
      });
    };
    $[16] = queryClient;
    $[17] = queryKey;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = {
      mutationFn: t4,
      onMutate: t5,
      onError: t6,
      onSettled: t7
    };
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const mutation = useMutation(t8);
  let t9;
  if ($[26] !== number || $[27] !== owner || $[28] !== repo) {
    t9 = async () => {
      const res_1 = await fetch(`/api/asana/associated-issue/delete?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}&number=${number}`, {
        method: "DELETE"
      });
      if (!res_1.ok) {
        throw new Error("Failed to delete Asana issue");
      }
      return (await res_1.json()) as AsanaAssociatedIssueDeleteResponse;
    };
    $[26] = number;
    $[27] = owner;
    $[28] = repo;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  let t11;
  let t12;
  if ($[30] !== queryClient || $[31] !== queryKey) {
    t10 = async () => {
      await queryClient.cancelQueries({
        queryKey
      });
      const previousIssue_0 = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, null);
      return {
        previousIssue: previousIssue_0
      };
    };
    t11 = (_err_0, _variables_0, context_0) => {
      if (context_0?.previousIssue !== undefined) {
        queryClient.setQueryData(queryKey, context_0.previousIssue);
      }
    };
    t12 = () => {
      queryClient.invalidateQueries({
        queryKey
      });
    };
    $[30] = queryClient;
    $[31] = queryKey;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
  } else {
    t10 = $[32];
    t11 = $[33];
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t9) {
    t13 = {
      mutationFn: t9,
      onMutate: t10,
      onError: t11,
      onSettled: t12
    };
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const deleteMutation = useMutation(t13);
  let t14;
  if ($[40] !== mutation) {
    t14 = params => mutation.mutate(params);
    $[40] = mutation;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const setIssue = t14;
  let t15;
  if ($[42] !== deleteMutation) {
    t15 = () => deleteMutation.mutate();
    $[42] = deleteMutation;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const deleteIssue = t15;
  const t16 = data ?? null;
  let t17;
  if ($[44] !== deleteIssue || $[45] !== deleteMutation || $[46] !== error || $[47] !== isError || $[48] !== isLoading || $[49] !== mutation || $[50] !== refetch || $[51] !== setIssue || $[52] !== t16) {
    t17 = {
      issue: t16,
      isLoading,
      isError,
      error,
      refetch,
      setIssue,
      mutation,
      deleteIssue,
      deleteMutation
    };
    $[44] = deleteIssue;
    $[45] = deleteMutation;
    $[46] = error;
    $[47] = isError;
    $[48] = isLoading;
    $[49] = mutation;
    $[50] = refetch;
    $[51] = setIssue;
    $[52] = t16;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
}